import type { RequestPayload } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import { Autocomplete, Button } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconFileImport } from "@tabler/icons-react";
import type { DataTableColumn, DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { ImportModal } from "../../../components/modals/ImportModal";
import { useYearContext } from "../../../hooks/useYearContext";
import type { ApprenticeSupervisor } from "../../../models/apprenticeSupervisor";
import type { Pagination } from "../../../models/pagination";
import {
  apprentice_supervisor_path,
  apprentice_supervisors_path,
  edit_apprentice_supervisor_path,
  import_apprentice_supervisors_path,
  import_example_apprentice_supervisors_path,
  new_apprentice_supervisor_path,
} from "../../../routes";
import Users, { resetUserFilter, type UserFilterFormType } from "../Users";

type ApprenticeSupervisorProps = {
  apprenticeSupervisors: ApprenticeSupervisor[];
  supervisor_names: string[];
  company_names: string[];
  filter: FilterFormType & {
    sort_key?: "name" | "company.name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = UserFilterFormType & {
  company_name?: string;
};

const resetFilters: FilterFormType = {
  company_name: undefined,
  ...resetUserFilter,
};

export default function ApprenticeSupervisors({
  apprenticeSupervisors,
  company_names,
  filter,
  supervisor_names,
  pagination,
}: ApprenticeSupervisorProps) {
  const { pathWithYearContext } = useYearContext();
  const { sort_key, sort_direction, ...filterFormData } = filter;
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const sortStatus: DataTableSortStatus<ApprenticeSupervisor> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const onSortChange = (sortStatus: DataTableSortStatus<ApprenticeSupervisor>) =>
    handlePageUpdate(filterFormData, sortStatus, pagination.page);
  const onFilterSubmit = (values: FilterFormType) => handlePageUpdate(values, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(filterFormData, sortStatus, page);

  const handlePageUpdate = (filters: FilterFormType, sortStatus: DataTableSortStatus<ApprenticeSupervisor>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(apprentice_supervisors_path), {
      only: ["apprenticeSupervisors", "filter", "pagination"],
      data: {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };

  const onView = (apprenticeSupervisors: ApprenticeSupervisor) =>
    router.get(pathWithYearContext(apprentice_supervisor_path, apprenticeSupervisors.id));
  const onDelete = (apprenticeSupervisors: ApprenticeSupervisor) =>
    router.delete(pathWithYearContext(apprentice_supervisor_path, apprenticeSupervisors.id));
  const onEdit = (apprenticeSupervisors: ApprenticeSupervisor) =>
    router.get(pathWithYearContext(edit_apprentice_supervisor_path, apprenticeSupervisors.id));
  const onNew = () => router.get(pathWithYearContext(new_apprentice_supervisor_path));
  const onImport = (values: RequestPayload) => {
    router.post(pathWithYearContext(import_apprentice_supervisors_path), values, {
      onFinish: () => {
        setIsLoading(false);
        close();
      },
    });
  };
  const additionalColumns: DataTableColumn<ApprenticeSupervisor>[] = [
    {
      accessor: "company.name",
      sortable: true,
      title: "Lehrbetrieb",
    },
  ];

  const getAdditionalFilters = (filterForm: UseFormReturnType<FilterFormType>) => [
    <Autocomplete
      label="Lehrbetrieb"
      clearable
      data={company_names}
      key={filterForm.key("company_name")}
      {...filterForm.getInputProps("company_name")}
    />,
  ];

  const actionButtons = [
    <Button key="apprentice-supervisor-import" leftSection={<IconFileImport />} onClick={() => open()} variant="light">
      Berufsbildner/innen importieren
    </Button>,
  ];

  return (
    <>
      <ImportModal
        opened={opened}
        close={close}
        title="Berufsbildner/innen importieren"
        onSubmit={onImport}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        downloadPath={pathWithYearContext(import_example_apprentice_supervisors_path)}
      />
      <Users<ApprenticeSupervisor, FilterFormType>
        actionButtons={actionButtons}
        getAdditionalFilters={getAdditionalFilters}
        onFilterSubmit={onFilterSubmit}
        filterFormData={filterFormData}
        additionalColumns={additionalColumns}
        title="Berufsbildner/in"
        records={apprenticeSupervisors}
        userNames={supervisor_names}
        onNew={onNew}
        onView={onView}
        onDelete={onDelete}
        onEdit={onEdit}
        onReset={() => onFilterSubmit(resetFilters)}
        sortStatus={sortStatus}
        onSortStatusChange={onSortChange}
        userTypeString="Berufsbildner/in"
        userTypeStringPlural="Berufsbildner/in"
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </>
  );
}
